import axios from "axios";
import { getCsrfHeader } from "../utils/csrf";
import {
  createCircuitBreaker,
  createCircuitBreakerError,
  CIRCUIT_BREAKER_MESSAGES,
} from "../utils/circuitBreaker";
import { parseEnvInt } from "../utils/env";
import { MODEL_FAMILIES } from "../utils/constants";
import { STORAGE_KEYS, ROUTES } from "../config/constants";

const API_URL = process.env.REACT_APP_API_URL || "/api";

// Track if we're currently refreshing to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let failedQueue = [];

// ============================================================================
// Circuit Breaker
// ============================================================================

// Circuit breaker configuration can be overridden via environment variables
const circuitBreakerConfig = {
  failureThreshold: parseEnvInt(
    process.env.REACT_APP_CIRCUIT_BREAKER_THRESHOLD,
    5,
  ),
  resetTimeoutMs: parseEnvInt(
    process.env.REACT_APP_CIRCUIT_BREAKER_TIMEOUT_MS,
    30000,
  ),
};

const circuitBreaker = createCircuitBreaker("API", circuitBreakerConfig);

// ============================================================================
// Request Deduplication
// ============================================================================

// Track in-flight GET requests to prevent duplicate calls
const inflightRequests = new Map();

/**
 * Generate a simple hash fingerprint from a string.
 * Used to create cache keys without exposing raw token values.
 * This is NOT cryptographically secure - only for deduplication keys.
 * @param {string} str - String to hash
 * @returns {string} 8-character hash fingerprint
 */
function hashFingerprint(str) {
  if (!str) return "none";
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.codePointAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  // Convert to base36 for compact representation, take last 8 chars
  return Math.abs(hash).toString(36).slice(-8).padStart(8, "0");
}

const processQueue = (error) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });
  failedQueue = [];
};

// Create axios instance with default config.
// withCredentials: true tells the browser to include the httpOnly auth cookies
// (access_token, refresh_token) on every same-origin request automatically.
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

// Add request interceptor for circuit breaker, CSRF, and deduplication.
// The access_token cookie is sent automatically by the browser (withCredentials:
// true) — no manual Authorization header injection is needed here.
apiClient.interceptors.request.use(
  (config) => {
    // Check circuit breaker first
    if (!circuitBreaker.check()) {
      return Promise.reject(
        createCircuitBreakerError(CIRCUIT_BREAKER_MESSAGES.SERVICE_UNAVAILABLE),
      );
    }

    // Add CSRF header for state-changing requests
    if (
      ["post", "put", "patch", "delete"].includes(config.method?.toLowerCase())
    ) {
      const csrfHeader = getCsrfHeader();
      config.headers = {
        ...config.headers,
        ...csrfHeader,
      };
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// Wrap the GET method for request deduplication
// Note: Requests with AbortSignals are excluded from deduplication because
// different callers may abort independently, and sharing promises would cause
// one caller's abort to incorrectly reject another caller's request.
const originalGet = apiClient.get.bind(apiClient);
apiClient.get = function (url, config = {}) {
  // Skip deduplication for requests with abort signals - they have caller-specific lifecycle
  if (config.signal) {
    return originalGet(url, config);
  }

  // Include a user fingerprint in the cache key to prevent sharing cached
  // responses across different auth states (different users, or logged-in vs
  // not).  The user object in localStorage changes on login/logout — the same
  // events that should invalidate the cache — so it is a reliable auth-state
  // discriminator.  The token is never in localStorage (httpOnly cookies), so
  // it cannot be used here.
  const userStr = localStorage.getItem(STORAGE_KEYS.USER);
  const tokenFingerprint = hashFingerprint(userStr);
  const paramsFingerprint = hashFingerprint(
    JSON.stringify(config.params || {}),
  );
  const requestKey = `GET:${url}:params=${paramsFingerprint}:auth=${tokenFingerprint}`;

  // Check for existing in-flight request
  const existingRequest = inflightRequests.get(requestKey);
  if (existingRequest) {
    return existingRequest;
  }

  // Make the request and track it
  const requestPromise = originalGet(url, config).finally(() => {
    inflightRequests.delete(requestKey);
  });

  inflightRequests.set(requestKey, requestPromise);
  return requestPromise;
};

// Clear in-flight request cache after mutations to prevent stale data
// This is called after POST/PUT/PATCH/DELETE requests succeed
// Also exported for use during logout to prevent stale cache entries
export function clearInflightCache() {
  inflightRequests.clear();
}

// Add response interceptor: unwrap standard { status, data } envelope & handle errors
apiClient.interceptors.response.use(
  (response) => {
    // Clear GET cache after successful mutations to prevent stale data
    const method = response.config?.method?.toLowerCase();
    if (["post", "put", "patch", "delete"].includes(method)) {
      clearInflightCache();
    }

    // Backend wraps successful responses in { status: "success", data: { ... } }.
    // Unwrap so callers receive the inner payload directly from api methods.
    const body = response.data;
    if (body?.status === "success" && body.data !== undefined) {
      response.data = body.data;
    }

    // Record success for circuit breaker AFTER processing completes successfully
    circuitBreaker.recordSuccess();

    return response;
  },
  async (error) => {
    // Guard against null/undefined error — can occur when requests are aborted
    // before axios fully constructs the error object
    if (!error) {
      throw new Error("Request failed");
    }

    // Circuit breaker errors should pass through
    if (error.isCircuitBreakerError) {
      throw error;
    }

    // Deliberately canceled requests (AbortController / navigation) are not errors
    if (
      error.name === "AbortError" ||
      error.name === "CanceledError" ||
      error.code === "ERR_CANCELED"
    ) {
      throw error;
    }

    // Record failure for circuit breaker (for server errors)
    const status = error.response?.status;
    if (status >= 500 || status === 429 || !error.response) {
      circuitBreaker.recordFailure();
    }

    const originalRequest = error.config;

    // Handle 401 Unauthorized — try to silently refresh the access token first.
    // Tokens live in httpOnly cookies so this is just a POST to /auth/refresh;
    // the browser sends the refresh_token cookie automatically.  On success the
    // server sets a new access_token cookie, and the retried request will carry
    // it without any JavaScript-side token management.
    if (error.response?.status === 401 && !originalRequest._retry) {
      // If this 401 came from the refresh endpoint itself the session is truly
      // over — clear local state and send the user to login.
      if (originalRequest.url?.includes("/auth/refresh")) {
        localStorage.removeItem(STORAGE_KEYS.USER);
        globalThis.location.href = ROUTES.LOGIN;
        throw error;
      }

      // A refresh is already in flight — queue this request so it retries once
      // the new cookie is set, rather than firing another refresh.
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => apiClient(originalRequest))
          .catch((err) => {
            throw err;
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // withCredentials sends the refresh_token cookie; the server sets a
        // new access_token cookie in the response.  No tokens pass through JS.
        await axios.post(
          `${API_URL}/auth/refresh`,
          {},
          { withCredentials: true },
        );

        processQueue(null);
        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        localStorage.removeItem(STORAGE_KEYS.USER);
        globalThis.location.href = ROUTES.LOGIN;
        throw refreshError;
      } finally {
        isRefreshing = false;
      }
    }

    // Log errors in development only
    if (process.env.NODE_ENV === "development") {
      console.error("API Error:", error.response?.data || error.message);
    }

    // Send to error tracking in production (Sentry, LogRocket, etc.).
    // globalThis.errorTracker must expose captureException(error): void.
    // It is initialised by the application shell (index.js or App.js) before
    // any components mount — e.g. via Sentry.init().  If absent, this is skipped.
    if (process.env.NODE_ENV === "production" && globalThis.errorTracker) {
      globalThis.errorTracker.captureException(error);
    }

    throw error;
  },
);

// Widget API methods
export const widgetApi = {
  // Get widget form config (allowed models, default model)
  getConfig: async ({ signal } = {}) => {
    const response = await apiClient.get("/widgets/config", { signal });
    return response.data;
  },

  // Get all widgets
  getWidgets: async (page = 1, limit = 10) => {
    const response = await apiClient.get("/widgets", {
      params: { page, limit },
    });
    return response.data;
  },

  // Get widget by ID
  getWidget: async (id, { signal } = {}) => {
    const response = await apiClient.get(`/widgets/${id}`, { signal });
    return response.data;
  },

  // Create new widget
  createWidget: async (widgetData) => {
    const response = await apiClient.post("/widgets", widgetData);
    return response.data;
  },

  // Update widget
  updateWidget: async (id, widgetData) => {
    const response = await apiClient.put(`/widgets/${id}`, widgetData);
    return response.data;
  },

  // Delete widget
  deleteWidget: async (id) => {
    const response = await apiClient.delete(`/widgets/${id}`);
    return response.data;
  },

  // Regenerate widget secret
  regenerateSecret: async (id) => {
    const response = await apiClient.post(`/widgets/${id}/regenerate-secret`);
    return response.data;
  },

  // Get embed code
  getEmbedCode: async (id) => {
    const response = await apiClient.get(`/widgets/${id}/embed-code`);
    return response.data;
  },
};

// Knowledge source API methods
export const knowledgeApi = {
  // Get knowledge sources for widget
  getKnowledgeSources: async (widgetId, { signal } = {}) => {
    const response = await apiClient.get(`/widgets/${widgetId}/knowledge`, {
      signal,
    });
    return response.data;
  },

  // Get a single knowledge source (includes content for text sources)
  getKnowledgeSource: async (widgetId, sourceId, { signal } = {}) => {
    const response = await apiClient.get(
      `/widgets/${widgetId}/knowledge/${sourceId}`,
      { signal },
    );
    return response.data;
  },

  // Upload file or add URL/text
  addKnowledgeSource: async (widgetId, formData) => {
    const response = await apiClient.post(
      `/widgets/${widgetId}/knowledge`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return response.data;
  },

  // Delete knowledge source
  deleteKnowledgeSource: async (widgetId, sourceId) => {
    const response = await apiClient.delete(
      `/widgets/${widgetId}/knowledge/${sourceId}`,
    );
    return response.data;
  },

  // Update knowledge source (name or content for text sources)
  updateKnowledgeSource: async (widgetId, sourceId, data) => {
    const response = await apiClient.put(
      `/widgets/${widgetId}/knowledge/${sourceId}`,
      data,
    );
    return response.data;
  },

  // Reprocess knowledge source
  reprocessKnowledgeSource: async (widgetId, sourceId) => {
    const response = await apiClient.put(
      `/widgets/${widgetId}/knowledge/${sourceId}/reprocess`,
    );
    return response.data;
  },
};

// Analytics API methods
export const analyticsApi = {
  // Get widget analytics
  getWidgetAnalytics: async (widgetId, startDate, endDate, { signal } = {}) => {
    const response = await apiClient.get(
      `/analytics/widgets/${widgetId}/analytics`,
      {
        params: { start_date: startDate, end_date: endDate },
        signal,
      },
    );
    return response.data;
  },

  // Get conversations
  getConversations: async (widgetId, page = 1, limit = 20, { signal } = {}) => {
    const response = await apiClient.get(
      `/analytics/widgets/${widgetId}/conversations`,
      {
        params: { page, limit },
        signal,
      },
    );
    return response.data;
  },

  // Get conversation messages
  getConversationMessages: async (
    widgetId,
    conversationId,
    { signal } = {},
  ) => {
    const response = await apiClient.get(
      `/analytics/widgets/${widgetId}/conversations/${conversationId}/messages`,
      { signal },
    );
    return response.data;
  },

  // Update conversation status (close or reopen)
  updateConversationStatus: async (widgetId, conversationId, status) => {
    const response = await apiClient.patch(
      `/analytics/widgets/${widgetId}/conversations/${conversationId}`,
      { status },
    );
    return response.data;
  },

  // Get usage summary
  getUsageSummary: async (startDate, endDate, { signal } = {}) => {
    const response = await apiClient.get("/analytics/usage/summary", {
      params: { start_date: startDate, end_date: endDate },
      signal,
    });
    return response.data;
  },
};

// Account / user API methods
export const accountApi = {
  // Get current authenticated user profile
  getCurrentUser: async ({ signal } = {}) => {
    const response = await apiClient.get("/auth/me", { signal });
    return response.data;
  },

  // Get current credit balances, thresholds, and low-balance warnings
  getCredits: async ({ signal } = {}) => {
    const response = await apiClient.get("/account/credits", { signal });
    return response.data;
  },

  // Get paginated credit transaction history
  getCreditHistory: async ({ family, page, limit, signal } = {}) => {
    const params = {};
    if (family) params.family = family;
    if (page != null) params.page = page;
    if (limit != null) params.limit = Math.min(limit, 100);
    const response = await apiClient.get("/account/credit-history", {
      params,
      signal,
    });
    return response.data;
  },

  // Update low-balance warning thresholds (only known family keys forwarded)
  updateCreditThresholds: async (thresholds, { signal } = {}) => {
    // SEC-1: Derive from MODEL_FAMILIES instead of hardcoding
    const sanitized = {};
    for (const { key } of MODEL_FAMILIES) {
      if (key in thresholds) sanitized[key] = thresholds[key];
    }
    const response = await apiClient.put(
      "/account/credit-thresholds",
      sanitized,
      { signal },
    );
    return response.data;
  },
};

// Re-exports from dedicated modules (backward compatibility)
export { adminApi } from "./adminApi";
export { billingApi } from "./billingApi";

export default apiClient;
